import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const DeleteAccount: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const [step, setStep] = useState<'form' | 'sent' | 'confirmed' | 'error'>('form');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const confirmDeletion = async () => {
      setSubmitting(true);
      setErrorMsg('');
      try {
        const res = await fetch(`${API_BASE}/users/confirm-deletion/${token}`, {
          method: 'DELETE',
        });
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok) {
          setStep('confirmed');
        } else {
          setStep('error');
          setErrorMsg(json.message || 'The deletion link is invalid or has expired.');
        }
      } catch {
        if (!cancelled) {
          setStep('error');
          setErrorMsg('Network error. Please request a new deletion link.');
        }
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    };
    confirmDeletion();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setErrorMsg('Please enter your email address.');
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await fetch(`${API_BASE}/users/request-deletion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const json = await res.json();
      if (res.ok) {
        setStep('sent');
      } else {
        setErrorMsg(json.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ py: 8, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <DeleteForeverIcon sx={{ fontSize: 56, color: 'error.main', mb: 1 }} />
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Delete Your Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We're sorry to see you go. This page lets you request permanent deletion of your
            Kundlistar account — even without the app installed.
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* What gets deleted */}
        <Card variant="outlined" sx={{ mb: 4, borderColor: 'warning.main' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              What will be deleted
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>Your account credentials (name, email, password)</li>
              <li>Your subscription history</li>
              <li>Your personal birth information</li>
              <li>All notification and preference settings</li>
            </Typography>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mt: 2 }}>
              What is anonymised (not deleted)
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>
                Saved birth charts — the <strong>planetary calculation data</strong> is retained for
                astronomical integrity but all personal identifiers (name, exact date, location) are
                replaced with anonymous placeholders.
              </li>
            </Typography>
          </CardContent>
        </Card>

        {/* Steps indicator */}
        <Stepper activeStep={step === 'form' ? 0 : step === 'sent' ? 1 : 2} sx={{ mb: 4 }}>
          <Step><StepLabel>Enter Email</StepLabel></Step>
          <Step><StepLabel>Confirm via Email</StepLabel></Step>
          <Step><StepLabel>Done</StepLabel></Step>
        </Stepper>

        {/* Step: form */}
        {step === 'form' && (
          <Box component="form" onSubmit={handleRequest}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter the email address associated with your Kundlistar account. We will send you a
              one-time confirmation link.
            </Typography>
            {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 3 }}
              autoComplete="email"
            />
            <Button
              type="submit"
              variant="contained"
              color="error"
              fullWidth
              size="large"
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Send Deletion Confirmation Email'}
            </Button>
          </Box>
        )}

        {token && submitting && (
          <Alert severity="info" icon={false} sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>Deleting account</Typography>
            <Typography variant="body2">Please wait while we confirm your deletion link.</Typography>
          </Alert>
        )}

        {/* Step: email sent */}
        {step === 'sent' && (
          <Alert severity="info" icon={false} sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>Check your inbox</Typography>
            <Typography variant="body2">
              We sent a confirmation link to <strong>{email}</strong>. Click the link in the email
              to permanently delete your account. The link expires in <strong>1 hour</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
              If you don't see the email, check your spam folder.
            </Typography>
          </Alert>
        )}

        {/* Step: confirmed (if backend redirects back here) */}
        {step === 'confirmed' && (
          <Alert severity="success" sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>Account Deleted</Typography>
            <Typography variant="body2">
              Your Kundlistar account has been permanently deleted. Thank you for using our service.
            </Typography>
          </Alert>
        )}

        {step === 'error' && (
          <Alert severity="error" sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>Deletion Link Not Valid</Typography>
            <Typography variant="body2">{errorMsg}</Typography>
            <Button
              variant="contained"
              color="error"
              sx={{ mt: 2 }}
              onClick={() => {
                setStep('form');
                setErrorMsg('');
              }}
            >
              Request New Link
            </Button>
          </Alert>
        )}

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Changed your mind?{' '}
          <a href="/" style={{ color: 'inherit' }}>
            Return to Kundlistar
          </a>
        </Typography>
      </Container>
    </Box>
  );
};

export default DeleteAccount;
